/**
 * Combat Effects Configuration
 * 
 * Defines visual effects for different weapon rarities and signature move elements.
 * Effects scale in intensity and color based on weapon power tier.
 */

export type WeaponRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type SignatureElement = 'flame' | 'storm' | 'tide' | 'shadow' | 'iron' | 'spirit';

export interface EffectConfig {
  primaryColor: number;
  secondaryColor: number;
  tertiaryColor: number;
  particleCount: number;
  particleSize: number;
  muzzleFlashScale: number;
  trailDensity: number;
  impactIntensity: number;
  screenShakeAmount: number;
}

export const RARITY_EFFECT_CONFIG: Record<WeaponRarity, EffectConfig> = {
  common: {
    primaryColor: 0xcccccc,
    secondaryColor: 0x999999,
    tertiaryColor: 0x666666,
    particleCount: 2,
    particleSize: 4,
    muzzleFlashScale: 1.0,
    trailDensity: 0.5,
    impactIntensity: 1.0,
    screenShakeAmount: 0.002,
  },
  uncommon: {
    primaryColor: 0x4caf50,
    secondaryColor: 0x45a049,
    tertiaryColor: 0x3d8b40,
    particleCount: 4,
    particleSize: 5,
    muzzleFlashScale: 1.2,
    trailDensity: 0.7,
    impactIntensity: 1.2,
    screenShakeAmount: 0.003,
  },
  rare: {
    primaryColor: 0x2196f3,
    secondaryColor: 0x1976d2,
    tertiaryColor: 0x1565c0,
    particleCount: 6,
    particleSize: 6,
    muzzleFlashScale: 1.4,
    trailDensity: 0.9,
    impactIntensity: 1.4,
    screenShakeAmount: 0.004,
  },
  epic: {
    primaryColor: 0x9c27b0,
    secondaryColor: 0x8e24aa,
    tertiaryColor: 0x7b1fa2,
    particleCount: 8,
    particleSize: 7,
    muzzleFlashScale: 1.6,
    trailDensity: 1.1,
    impactIntensity: 1.6,
    screenShakeAmount: 0.005,
  },
  legendary: {
    primaryColor: 0xffd700,
    secondaryColor: 0xffb300,
    tertiaryColor: 0xff9800,
    particleCount: 12,
    particleSize: 8,
    muzzleFlashScale: 2.0,
    trailDensity: 1.3,
    impactIntensity: 2.0,
    screenShakeAmount: 0.008,
  },
};

export const ELEMENT_EFFECT_CONFIG: Record<SignatureElement, EffectConfig> = {
  flame: {
    primaryColor: 0xff6b35,
    secondaryColor: 0xff4500,
    tertiaryColor: 0xffa500,
    particleCount: 10,
    particleSize: 7,
    muzzleFlashScale: 1.8,
    trailDensity: 1.2,
    impactIntensity: 1.8,
    screenShakeAmount: 0.006,
  },
  storm: {
    primaryColor: 0x00bfff,
    secondaryColor: 0x7df9ff,
    tertiaryColor: 0x87ceeb,
    particleCount: 8,
    particleSize: 6,
    muzzleFlashScale: 1.6,
    trailDensity: 1.0,
    impactIntensity: 1.6,
    screenShakeAmount: 0.005,
  },
  tide: {
    primaryColor: 0x1e90ff,
    secondaryColor: 0x0066cc,
    tertiaryColor: 0x00bfff,
    particleCount: 7,
    particleSize: 6,
    muzzleFlashScale: 1.5,
    trailDensity: 0.9,
    impactIntensity: 1.5,
    screenShakeAmount: 0.004,
  },
  shadow: {
    primaryColor: 0x4b0082,
    secondaryColor: 0x2f0047,
    tertiaryColor: 0x1a0033,
    particleCount: 9,
    particleSize: 7,
    muzzleFlashScale: 1.7,
    trailDensity: 1.1,
    impactIntensity: 1.7,
    screenShakeAmount: 0.005,
  },
  iron: {
    primaryColor: 0xc0c0c0,
    secondaryColor: 0x808080,
    tertiaryColor: 0x505050,
    particleCount: 6,
    particleSize: 8,
    muzzleFlashScale: 1.4,
    trailDensity: 0.8,
    impactIntensity: 1.4,
    screenShakeAmount: 0.004,
  },
  spirit: {
    primaryColor: 0x9932cc,
    secondaryColor: 0x8b008b,
    tertiaryColor: 0x4b0082,
    particleCount: 11,
    particleSize: 7,
    muzzleFlashScale: 1.9,
    trailDensity: 1.2,
    impactIntensity: 1.9,
    screenShakeAmount: 0.006,
  },
};

export const getEffectConfig = (
  rarity?: WeaponRarity,
  element?: SignatureElement
): EffectConfig => {
  if (element && ELEMENT_EFFECT_CONFIG[element]) {
    return ELEMENT_EFFECT_CONFIG[element];
  }
  if (rarity && RARITY_EFFECT_CONFIG[rarity]) {
    return RARITY_EFFECT_CONFIG[rarity];
  }
  return RARITY_EFFECT_CONFIG.common;
};

// ============ PROGRESSIVE ENHANCEMENT SYSTEM ============

/**
 * Visual intensity multipliers based on weapon rarity
 * These determine which enhancement layers are active
 */
export const RARITY_INTENSITY_MULTIPLIERS: Record<WeaponRarity, number> = {
  common: 1.0,      // Level 1-9: Baseline (current implementation)
  uncommon: 1.2,    // Level 10-24: Subtle enhancements
  rare: 1.4,        // Level 25-39: Multi-layered effects
  epic: 1.6,        // Level 40-49: Advanced effects
  legendary: 2.0,   // Level 50+: Maximum intensity
};

/**
 * Element intensity bonus when signature moves are equipped
 */
export const ELEMENT_INTENSITY_BONUS = 1.3; // 30% boost

/**
 * Calculate visual intensity based on weapon rarity and signature element
 * Returns a multiplier that determines which enhancement layers are active
 * 
 * @param weaponRarity - The weapon rarity tier (common to legendary)
 * @param signatureElement - Optional signature element if equipped
 * @returns Visual intensity multiplier (1.0 = baseline, 2.0+ = maximum)
 */
export function calculateVisualIntensity(
  weaponRarity?: WeaponRarity,
  signatureElement?: SignatureElement
): number {
  // Base intensity from rarity (defaults to common = 1.0)
  let intensity = weaponRarity 
    ? RARITY_INTENSITY_MULTIPLIERS[weaponRarity]
    : RARITY_INTENSITY_MULTIPLIERS.common;
  
  // Element bonus when signature move is equipped
  if (signatureElement) {
    intensity *= ELEMENT_INTENSITY_BONUS;
  }
  
  return intensity;
}

/**
 * Check if enhancements should be applied based on intensity threshold
 * 
 * @param intensity - Current visual intensity multiplier
 * @param threshold - Minimum intensity required for enhancement
 * @returns True if enhancement should be applied
 */
export function shouldApplyEnhancement(intensity: number, threshold: number): boolean {
  return intensity >= threshold;
}

/**
 * Enhancement thresholds for different effect tiers
 */
export const ENHANCEMENT_THRESHOLDS = {
  UNCOMMON: 1.2,    // Subtle enhancements (Level 10+)
  RARE: 1.4,        // Multi-layered effects (Level 25+)
  EPIC: 1.6,        // Advanced effects (Level 40+)
  LEGENDARY: 2.0,   // Maximum intensity (Level 50+)
} as const;
